import { Box, Divider, Grid, Theme, Typography } from '@mui/material';
import { CheckLetterStatusDialog } from '@/components/letter/statusCheck/CheckLetterStatusDialog';
import { CheckLetterStatusForm } from './CheckLetterStatusForm';
import { CheckLetterStatusHistory } from '@/components/letter/statusCheck/CheckLetterStatusHistory';
import { Helmet } from 'react-helmet-async';
import { ImageView, ImageViewItem, useImageView } from '@/app/hooks/useImageView';
import { ImageViewDialog } from '@/components/view/image/ImageViewDialog';
import { LetterStatusHistoryDTO } from '@/types/letter/LetterStatusHistoryDTO';
import { MESSAGE_CAPTCHA_FAILURE, MESSAGE_FETCH_ERROR } from '@/utils/validation-utils';
import { MetaType } from '@/types/MetaDTO';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { PhotoStatusImageDTO } from '@/types/photostatus/PhotoStatusImageDTO';
import { PhotoStatusItemDTO } from '@/types/photostatus/PhotoStatusItemDTO';
import { ROUTE_HELP_FEEDBACK } from '@/app/routes';
import { VerdictDot } from '@/components/letter/VerdictDot';
import { useCheckLetterStatusMutation, useCheckLetterStatusPhotoMutation } from '@/services/api/letterApiSlice';
import { useGetMetaDataQuery } from '@/services/api/metaApiSlice';
import { useReCaptcha } from '@/app/hooks/useReCaptcha';
import { useSnackbar } from 'notistack';
import React, { FC, Fragment, ReactElement, useState } from 'react';

export interface CheckLetterStatusBlockProps {
  isMobile: boolean;
}

type Props = CheckLetterStatusBlockProps;

export const CheckLetterStatusBlock: FC<Props> = (props: Props): ReactElement => {
  const { isMobile } = props;

  const { data: meta } = useGetMetaDataQuery(MetaType.INFO_CHECK);

  const navigate: NavigateFunction = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { pending: captchaPending, obtainToken } = useReCaptcha('checkLetterStatusAction');

  const [checkLetterStatus, { isLoading }] = useCheckLetterStatusMutation();
  const [checkLetterStatusPhoto] = useCheckLetterStatusPhotoMutation();

  const imageView: ImageView = useImageView();

  const handleSupportClick = (): void => {
    navigate(ROUTE_HELP_FEEDBACK);
  };

  const [openLetterStatusDialog, setOpenLetterStatusDialog] = useState<boolean>(false);
  const [response, setResponse] = useState<LetterStatusHistoryDTO>();

  /**
   * Reads the full-size bytes of one photo and shows it full screen. The page proves who it is the
   * same way it did for the status itself, so the read carries a fresh reCAPTCHA of its own.
   */
  const handlePhotoClick = async (photos: PhotoStatusItemDTO[], index: number): Promise<void> => {
    if (!response) {
      return;
    }
    await imageView.openList(
      photos.map(
        (photo: PhotoStatusItemDTO): ImageViewItem => ({
          name: `Фото № ${photo.photoNumber}`,
          declined: photo.declined,
          read: async (): Promise<string | undefined> => {
            const token: string | null = await obtainToken();
            if (!token) {
              throw new Error(MESSAGE_CAPTCHA_FAILURE);
            }
            const image: PhotoStatusImageDTO = await checkLetterStatusPhoto({
              letterNumber: response.letterNumber,
              email: response.email,
              photoId: photo.id,
              reCaptchaToken: token,
            }).unwrap();
            return image?.content;
          },
        }),
      ),
      index,
    );
  };

  const handleSubmitForm = async (email: string, letterNumber: number) => {
    setResponse(undefined);
    const token: string | null = await obtainToken();
    if (!token) {
      return;
    }
    await checkLetterStatus({
      letterNumber: letterNumber,
      email: email,
      reCaptchaToken: token,
    })
      .unwrap()
      .then((dto) => {
        setResponse(dto);
        if (isMobile) {
          setOpenLetterStatusDialog(true);
        }
      })
      .catch(() => {
        enqueueSnackbar(MESSAGE_FETCH_ERROR, {
          variant: 'error',
        });
      });
  };

  return (
    <Fragment>
      {meta && (
        <Helmet>
          <title>{meta.title}</title>
          <meta name={'description'} content={meta.description} />
          <meta name={'keywords'} content={meta.keywords} />
        </Helmet>
      )}
      <Grid container={true} direction={'column'} sx={{ height: '100%' }} marginBottom={isMobile ? 5 : 0}>
        <Grid item={true} paddingBottom={isMobile ? 6 : 7}>
          {isMobile ? (
            <Grid container={true} direction={'column'}>
              <Grid item={true} marginTop={-0.5} paddingBottom={1.5}>
                <Typography variant={'h5'} align={'center'} sx={{ color: (theme: Theme) => theme.colors.grayText }}>
                  {'Проверка статуса письма'}
                </Typography>
              </Grid>
              <Grid item={true}>
                <Typography variant={'h1'} lineHeight={'32px'}>
                  {'Данные о письме'}
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <Typography variant={'h1'}>{'Проверка статуса письма'}</Typography>
          )}
        </Grid>
        <Grid item={true} xs={isMobile}>
          <CheckLetterStatusForm
            isLoading={isLoading}
            captchaPending={captchaPending}
            handleSubmitForm={handleSubmitForm}
            isMobile={isMobile}
          />
        </Grid>
        {!isMobile && response && (
          <Grid container={true} direction={'column'} spacing={3} pt={4} pb={4}>
            <Grid item={true}>
              <Divider />
            </Grid>
            <Grid item={true}>
              <CheckLetterStatusHistory
                events={response.events}
                photos={response.photos}
                eventLogLoading={isLoading}
                letterNumber={response.letterNumber}
                email={response.email}
                responseStatus={response.responseStatus}
                onPhotoClick={handlePhotoClick}
              />
            </Grid>
          </Grid>
        )}
      </Grid>
      {isMobile && response && (
        <CheckLetterStatusDialog
          open={openLetterStatusDialog}
          events={response.events}
          photos={response.photos}
          letterNumber={response.letterNumber}
          email={response.email}
          responseStatus={response.responseStatus}
          onClose={() => setOpenLetterStatusDialog(false)}
          handleSupportClick={handleSupportClick}
          onPhotoClick={handlePhotoClick}
        />
      )}
      <ImageViewDialog
        open={imageView.isOpen}
        isLoading={imageView.isLoading}
        isFailed={imageView.isFailed}
        content={imageView.content || ''}
        onPreviousImage={imageView.hasPrevious ? imageView.previous : undefined}
        onNextImage={imageView.hasNext ? imageView.next : undefined}
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {imageView.declined !== undefined && <VerdictDot declined={imageView.declined} />}
            <Typography variant={'h1'} fontSize={'24px'} lineHeight={'20px'}>
              {imageView.name}
            </Typography>
          </Box>
        }
        onClose={imageView.close}
      />
    </Fragment>
  );
};
